<script setup lang="ts">
import { computed } from 'vue';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useLazyOverflowTooltip } from '@/composables/useLazyOverflowTooltip';

/**
 * Shared "truncate + tooltip-the-overflow" cell wrapper.
 *
 * LAZY BY DESIGN: a results table is hundreds of cells, and eagerly mounting a reka-ui
 * Tooltip stack (+ a ResizeObserver via useOverflowTooltip) for every one of them is what
 * froze the UI on load — the cost is per-cell, not per-row, so it's independent of payload
 * size and hits every table page. Here the default render is just a plain truncated <span>;
 * the Tooltip machinery is only instantiated the first time a cell is actually hovered or
 * keyboard-focused AND its text overflows. Activation goes through useLazyTooltip, which waits
 * the normal open delay and never opens once the pointer has left. Cells the user never hovers cost nothing beyond the span.
 *
 * Once activated, behaviour is identical to the old inline pattern — it reuses
 * useOverflowTooltip's controlled open + deferred overflow measure + stuck-tooltip safety nets.
 *
 * Slots: default = visible (truncated) content; `tooltip` = tooltip body (defaults to default).
 */
const props = withDefaults(
  defineProps<{
    /** Pass the display value so the tooltip re-measures when the cell's content changes. */
    deps?: unknown;
    /**
     * Element to render the wrapper as. `div` suits a table cell; pass the tag
     * the surrounding markup actually calls for (`h2` for a panel heading) so
     * the wrapper carries the semantics instead of burying a `div` inside them.
     */
    as?: string;
    /**
     * Element to render the TRIGGER as — the truncating box that gets measured.
     *
     * Default `span`. Pass `button` when the truncated text is itself the
     * control, rather than putting a `<button>` inside the trigger: Chrome
     * blockifies a `<button>` to `inline-block` whatever `display` is asked
     * for, and an atomic inline box cannot be ellipsised — the text is
     * hard-clipped mid-word and, worse, the trigger's `scrollWidth` never
     * exceeds its `clientWidth`, so the tooltip that would have rescued the
     * hidden text never opens either. Making the button the trigger fixes both.
     *
     * Fallthrough attributes (`@click`, `data-testid`, `aria-*`, `type`) land
     * on the trigger rather than the wrapper, so the control keeps them.
     */
    triggerAs?: string;
    triggerClass?: string;
    contentClass?: string;
    side?: 'top' | 'right' | 'bottom' | 'left';
    align?: 'start' | 'center' | 'end';
    collisionPadding?: number;
  }>(),
  {
    deps: undefined,
    as: 'div',
    triggerAs: 'span',
    triggerClass: '',
    contentClass: 'max-w-xs break-words pointer-events-none',
    side: undefined,
    align: undefined,
    collisionPadding: undefined,
  },
);

// Fallthrough attrs belong to the trigger — it is the control and the thing a
// test or a screen reader is looking for; the wrapper is only a layout box.
defineOptions({ inheritAttrs: false });

const depsRef = computed(() => props.deps);
const {
  activated,
  probeRef,
  onProbePointerOver,
  onProbePointerOut,
  onProbeFocusIn,
  onProbeFocusOut,
  textRef,
  tooltipOpen,
  onOpenChange,
} = useLazyOverflowTooltip(depsRef);

/**
 * `-1` keeps a plain text trigger focusable enough to raise its own tooltip
 * without adding a tab stop for every cell in a table. A trigger that is
 * already a control has its own place in the tab order and must keep it.
 */
const triggerTabindex = computed(() => (props.triggerAs === 'span' ? -1 : undefined));
</script>

<template>
  <component :is="as" class="min-w-0 w-full max-w-full">
    <component
      :is="triggerAs"
      v-if="!activated"
      ref="probeRef"
      v-bind="$attrs"
      :tabindex="triggerTabindex"
      :class="['block w-full min-w-0 max-w-full truncate', triggerClass]"
      @pointerover="onProbePointerOver"
      @pointerout="onProbePointerOut"
      @focusin="onProbeFocusIn"
      @focusout="onProbeFocusOut"
    ><slot /></component>

    <Tooltip v-else :open="tooltipOpen" @update:open="onOpenChange">
      <TooltipTrigger as-child>
        <component
          :is="triggerAs"
          ref="textRef"
          v-bind="$attrs"
          :tabindex="triggerTabindex"
          :class="['block w-full min-w-0 max-w-full truncate', triggerClass]"
        ><slot /></component>
      </TooltipTrigger>
      <TooltipContent
        :class="contentClass"
        :side="side"
        :align="align"
        :collision-padding="collisionPadding"
      >
        <p class="pointer-events-none"><slot name="tooltip"><slot /></slot></p>
      </TooltipContent>
    </Tooltip>
  </component>
</template>
